import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { apiUrl } from '../config/api';
import { fetchAllUsers } from '../slices/authSlice';
import { AppDispatch, RootState } from '../store/store';

interface TimeEntry {
  startTime: string;
  endTime?: string;
  pausedDuration: number;
}

interface ChangeRequest {
  _id: string;
  userId: { _id: string; username: string };
  entryId: TimeEntry;
  originalStartTime?: string;
  originalEndTime?: string;
  originalPausedDuration?: number;
  requestedStartTime: string;
  requestedEndTime: string;
  requestedPausedDuration: number;
  comment?: string;
  status: 'pending' | 'approved' | 'declined';
  createdAt: string;
}

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatPause = (seconds?: number) => `${Math.round((seconds || 0) / 60)} min`;

const TimeEntryChangeRequestsPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.auth.user);
  const allUsers = useSelector((state: RootState) => state.auth.allUsers || []);
  const isAdmin = user?.role === 'admin';
  const [requests, setRequests] = useState<ChangeRequest[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number | 'all'>('all');
  const [showReviewedRequests, setShowReviewedRequests] = useState(true);
  const navigate = useNavigate();

  const fetchRequests = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const res = await fetch(apiUrl('/api/time-entry-change-requests'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Fehler beim Laden der Zeitänderungsanträge');
      setRequests(await res.json());
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Unbekannter Fehler');
    }
  }, [navigate]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  useEffect(() => {
    if (isAdmin) {
      dispatch(fetchAllUsers());
    }
  }, [dispatch, isAdmin]);

  useEffect(() => {
    setSelectedUserId('');
  }, [isAdmin]);

  const availableYears = useMemo(() => {
    const years = new Set<number>([currentYear]);

    requests.forEach((request) => {
      const requestedYear = new Date(request.requestedStartTime).getFullYear();
      const originalYear = new Date(request.originalStartTime || request.entryId?.startTime).getFullYear();
      const createdYear = new Date(request.createdAt).getFullYear();

      if (!Number.isNaN(requestedYear)) years.add(requestedYear);
      if (!Number.isNaN(originalYear)) years.add(originalYear);
      if (!Number.isNaN(createdYear)) years.add(createdYear);
    });

    return Array.from(years).sort((a, b) => b - a);
  }, [currentYear, requests]);

  const filteredRequests = useMemo(() => {
    return requests
      .filter((request) => {
        const requestUserId = request.userId?._id;
        const requestedYear = new Date(request.requestedStartTime).getFullYear();
        const originalYear = new Date(request.originalStartTime || request.entryId?.startTime).getFullYear();
        const matchesUser = !isAdmin || !selectedUserId || requestUserId === selectedUserId;
        const matchesYear =
          selectedYear === 'all' || requestedYear === selectedYear || originalYear === selectedYear;
        const matchesReviewStatus = showReviewedRequests || request.status === 'pending';

        return matchesUser && matchesYear && matchesReviewStatus;
      })
      .sort((a, b) => {
        const dateDiff =
          new Date(b.requestedStartTime).getTime() - new Date(a.requestedStartTime).getTime();
        if (dateDiff !== 0) return dateDiff;
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
  }, [isAdmin, requests, selectedUserId, selectedYear, showReviewedRequests]);

  const handleAction = async (id: string, action: 'approve' | 'decline') => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await fetch(
        apiUrl(`/api/time-entry-change-requests/${id}/${action}`),
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!res.ok) throw new Error('Aktion konnte nicht gespeichert werden');
      await fetchRequests();
    } catch (err: any) {
      setError(err.message || 'Unbekannter Fehler bei der Aktion');
    }
  };

  return (
    <div className="container">
      <h2>Zeitänderungsanträge</h2>
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="d-flex gap-3 flex-wrap mt-3 mb-3">
        {isAdmin && (
          <div style={{ minWidth: 260 }}>
            <label className="form-label">Nutzer</label>
            <select
              className="form-select"
              value={selectedUserId}
              onChange={(event) => setSelectedUserId(event.target.value)}
            >
              <option value="">Gesamt</option>
              {allUsers.map((currentUser) => (
                <option key={currentUser._id} value={currentUser._id}>
                  {currentUser.username}
                </option>
              ))}
            </select>
          </div>
        )}
        <div style={{ width: 140 }}>
          <label className="form-label">Jahr</label>
          <select
            className="form-select"
            value={selectedYear}
            onChange={(event) =>
              setSelectedYear(event.target.value === 'all' ? 'all' : Number(event.target.value))
            }
          >
            <option value="all">Gesamt</option>
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <label className="form-check report-export-option align-self-end mb-2" htmlFor="change-requests-show-approved">
          <input
            className="form-check-input report-export-checkbox"
            type="checkbox"
            id="change-requests-show-approved"
            checked={showReviewedRequests}
            onChange={(event) => setShowReviewedRequests(event.target.checked)}
          />
          <span className="form-check-label">
            Genehmigte und abgelehnte Anträge anzeigen
          </span>
        </label>
      </div>

      {filteredRequests.length === 0 && <p>Keine Zeitänderungsanträge vorhanden.</p>}

      <div className="change-request-list">
        {filteredRequests.map((request) => {
          const originalStartTime = request.originalStartTime || request.entryId?.startTime;
          const originalEndTime = request.originalEndTime || request.entryId?.endTime;
          const originalPausedDuration =
            request.originalPausedDuration ?? request.entryId?.pausedDuration;

          return (
            <div key={request._id} className="change-request-card">
              <div className="change-request-head">
                <div>
                  <strong className="change-request-user">{request.userId?.username || '-'}</strong>
                  <div className="change-request-date">Antrag vom {formatDateTime(request.createdAt)}</div>
                </div>
                <div>
                  {request.status === 'pending' && (
                    <span className="change-request-status status-pending">Offen</span>
                  )}
                  {request.status === 'approved' && (
                    <span className="change-request-status status-approved">Bestätigt</span>
                  )}
                  {request.status === 'declined' && (
                    <span className="change-request-status status-declined">Abgelehnt</span>
                  )}
                </div>
              </div>

              <div className="change-request-grid">
                <div className="change-request-section">
                  <div className="change-request-label">Vorher</div>
                  {formatDateTime(originalStartTime)} bis {formatDateTime(originalEndTime)}
                  <br />
                  Pause: {formatPause(originalPausedDuration)}
                </div>
                <div className="change-request-section">
                  <div className="change-request-label">Beantragt</div>
                  {formatDateTime(request.requestedStartTime)} bis {formatDateTime(request.requestedEndTime)}
                  <br />
                  Pause: {formatPause(request.requestedPausedDuration)}
                </div>
                <div className="change-request-section">
                  <div className="change-request-label">Kommentar</div>
                  {request.comment || '-'}
                </div>
              </div>

              {isAdmin && request.status === 'pending' && (
                <div className="change-request-actions">
                  <button
                    className="btn btn-success btn-sm"
                    onClick={() => handleAction(request._id, 'approve')}
                  >
                    Bestätigen
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleAction(request._id, 'decline')}
                  >
                    Ablehnen
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TimeEntryChangeRequestsPage;
